import { FunctionComponent, useEffect, useState } from 'react'
import {
  Dropdown,
  Input,
  Text,
  getTheme,
  useMediaQuery,
} from '@conexasaude/conexa-hero'

import useStyles from '../DataEditingForm/styles'
import useInternalStyle from './styles'
import { RenderIf } from '@/components/RenderIf'
import { Icon } from '@conexasaude/iconography/icons'
import { Controller, useFormContext } from 'react-hook-form'
import {
  PREGNANCY_DAYS_OPTIONS,
  PREGNANCY_WEEKS_OPTIONS,
} from '@/modules/Attendance/constants'
import { UserConfirmDataFormType } from '../ConfirmContactWrapper/types'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import DatePickerControl from '@/components/Form/HookFormControls/DatePickerControl'

const PregnantDataSection: FunctionComponent = () => {
  const theme = getTheme()
  const { clinic, user } = useAuthContext()
  const isMobile = useMediaQuery(`(max-width: ${theme.grid.breakpoint.sm})`)
  const [inputHidden, setInputHidden] = useState(false)
  const styles = useStyles()
  const internalStyle = useInternalStyle()
  const { register, formState, control } =
    useFormContext<UserConfirmDataFormType>()

  const handlePregnancyRequired = (label: string) => {
    return clinic?.flag?.HabilitarFluxoDadosGestacionais ? `${label}*` : label
  }

  useEffect(() => {
    document
      ?.getElementById('disabledInputLastMentrualPeriodDate')
      ?.addEventListener('keydown', function (event) {
        event.preventDefault()
      })
  }, [])

  return (
    <div>
      <div css={styles.sectionTitle}>
        <Text color={theme.colors.brand.primary[500]} variant="headingSmall">
          Dados da pessoa gestante
        </Text>
        <RenderIf condition={!isMobile}>
          <hr />
        </RenderIf>
        <RenderIf condition={isMobile}>
          <Icon
            onClick={() => setInputHidden(!inputHidden)}
            name={inputHidden ? 'arrow-down' : 'arrow-up'}
            size="medium"
            color={theme.colors.brand.primary[300]}
          />
        </RenderIf>
      </div>
      <RenderIf condition={!inputHidden || !isMobile}>
        <div css={styles.sectionGridContent}>
          <div css={styles.sectionFullGridCol}>
            <Text variant="subheading">Tempo de gestação</Text>
          </div>
          <RenderIf condition={!isMobile}>
            <div css={styles.sectionGridCol}>
              <Controller
                name="weeksPregnancy"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Dropdown
                    {...register('weeksPregnancy')}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    error={formState?.errors?.weeksPregnancy?.message}
                    label={handlePregnancyRequired('Semanas')}
                    placeholder="De 1 a 42"
                    fullWidth
                    data={PREGNANCY_WEEKS_OPTIONS}
                    disabled={!!user?.pregnantInformation?.weeksPregnancy}
                  />
                )}
              />
            </div>
            <div css={styles.sectionGridCol}>
              <Controller
                name="daysPregnancy"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Dropdown
                    {...register('daysPregnancy')}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    error={formState?.errors?.daysPregnancy?.message}
                    label={handlePregnancyRequired('Dias')}
                    placeholder="De 1 a 6"
                    fullWidth
                    data={PREGNANCY_DAYS_OPTIONS}
                    disabled={!!user?.pregnantInformation?.daysPregnancy}
                  />
                )}
              />
            </div>
          </RenderIf>
          <RenderIf condition={isMobile}>
            <div css={internalStyle.timeSectionFlexbox}>
              <Controller
                name="weeksPregnancy"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Dropdown
                    {...register('weeksPregnancy')}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    error={formState?.errors?.weeksPregnancy?.message}
                    label={handlePregnancyRequired('Semanas')}
                    placeholder="De 1 a 42"
                    fullWidth
                    data={PREGNANCY_WEEKS_OPTIONS}
                    disabled={!!user?.pregnantInformation?.weeksPregnancy}
                  />
                )}
              />
              <Controller
                name="daysPregnancy"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Dropdown
                    {...register('daysPregnancy')}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    error={formState?.errors?.daysPregnancy?.message}
                    label={handlePregnancyRequired('Dias')}
                    placeholder="De 1 a 6"
                    fullWidth
                    data={PREGNANCY_DAYS_OPTIONS}
                    disabled={!!user?.pregnantInformation?.daysPregnancy}
                  />
                )}
              />
            </div>
          </RenderIf>
          <div css={styles.sectionGridCol}>
            <RenderIf
              condition={!user?.pregnantInformation?.lastMenstrualPeriodDate}
            >
              <DatePickerControl
                name="lastMenstrualPeriodDate"
                control={control}
                maxDate={new Date()}
                label={handlePregnancyRequired('Data da última menstruação')}
                placeholder={
                  formState?.errors?.lastMenstrualPeriodDate?.message
                    ? ''
                    : 'Ex.: dd/mm/aaaa'
                }
                fullWidth
                error={
                  formState?.errors?.lastMenstrualPeriodDate?.type ===
                  'invalid_type'
                    ? null
                    : formState?.errors?.lastMenstrualPeriodDate?.message
                }
              />
            </RenderIf>
            <RenderIf
              condition={!!user?.pregnantInformation?.lastMenstrualPeriodDate}
            >
              <div css={internalStyle.fieldDisabledWithIcon}>
                <Input
                  id="disabledInputLastMentrualPeriodDate"
                  value={user?.pregnantInformation?.lastMenstrualPeriodDate}
                  icon="calendar"
                  label="Data da última menstruação*"
                  disabled
                  fullWidth
                />
              </div>
            </RenderIf>
          </div>
        </div>
      </RenderIf>
    </div>
  )
}

export { PregnantDataSection }
